/**
 * Fixture identity + ops-settings state for the `settings` page (U-16-g1).
 *
 * This module is a COPY of two server tables and of one server rule, and it says so in every name:
 *
 *  - `FIXTURE_OPS_SETTING_DEFAULTS` / `FIXTURE_OPS_SETTING_RANGES` mirror
 *    `acs_service/routers/system.py::OPS_SETTING_DEFAULTS` / `OPS_SETTING_RANGES`, and
 *    `FIXTURE_SETTINGS_INTENT_NOTE` is `SETTINGS_INTENT_NOTE` verbatim — the door puts that sentence in
 *    every settings payload and the page must show it, so the fixture that dropped it would let a UI
 *    pass on an answer the façade never sends.
 *  - `FIXTURE_CONSOLE_USER_ROWS` mirrors what `models.py::ConsoleUser` publishes: `user_id`, `username`,
 *    `role`, `status`, `created_at`, `updated_at` and **no secret field** — no `password_hash`, no
 *    `salt`, not even `password_kdf` (§۶; `identity.py::public_user` is the only shape a door may
 *    publish). Nothing here is a credential, and fixture mode contacts no server, so the demo rows carry
 *    no password to leak either (AGENTS invariant ۴).
 *  - `lastOwnerAllows` is a port of `acs_service/identity.py::last_owner_allows` (§۳-۲), cell for cell:
 *    it refuses exactly one shape — a write that takes an **active owner** off the board while no other
 *    active owner remains — and permits a promotion, a password rotation, an edit of an already-disabled
 *    row, and a write that leaves the caller an active owner. `USER_STATUSES` is
 *    `routers/identity.py::USER_STATUSES`.
 *
 * The refusal the page renders is the door's SENTENCE, not a UI paraphrase (`lastOwnerRefusal`), because
 * the rule lives in the server inside the write: the fixture does not prevent the attempt, it answers it
 * the way `PATCH`/`DELETE /ops/v1/users/{user_id}` does — 409 `last-owner` and the row left exactly as it
 * was. `test/settingsFixture.test.ts` re-reads both Python files and fails on a drift, which is what
 * keeps this a copy rather than a second policy.
 *
 * `FIXTURE_CONSOLE_USER_ROWS` deliberately holds TWO active owners. With one, the last-owner guard could
 * only ever refuse and the demo could not show the state that makes the same write succeed; the second
 * owner is what lets a test walk «disable one owner → allowed, the deployment still has a way in» and
 * then «disable the last one → the server's refusal». A deployment that holds one owner is reachable in
 * the demo by deleting or demoting the other, and the guard is evaluated per write, never cached.
 */

import type { ConsoleUser } from '../api/types';
import { USER_STATUSES } from '../api/types';

/** The four roles §۲ publishes — the same list `EffectivePermissions.roles` carries (no second copy). */
export const FIXTURE_ROLES = ['owner', 'admin', 'operator', 'user'] as const;

/**
 * The two optical thresholds, named once so a consumer does not have to re-guess them.
 *
 * `FIXTURE_OPS_SETTING_DEFAULTS` below is a `Record<string, number>` — under
 * `noUncheckedIndexedAccess` a lookup in it answers `number | undefined`, and every file that wanted
 * one of these two numbers would need its own fallback literal. This is that literal's only home, and
 * the table spreads it, so the settings mirror and the decline forecast cannot drift apart.
 */
export const FIXTURE_OPTICAL_THRESHOLDS = { warn: -25, critical: -27 } as const;

/**
 * The declared per-port ONU capacity the PON capacity door measures against (V7). Named
 * once for the same reason the thresholds are: the capacity mirror and the settings form
 * must not drift apart over the literal.
 */
export const FIXTURE_PON_CAPACITY = { default: 64, min: 1, max: 256 } as const;

/** `routers/system.py::OPS_SETTING_DEFAULTS` — the whitelisted keys and the value read before any write. */
export const FIXTURE_OPS_SETTING_DEFAULTS: Readonly<Record<string, number>> = {
  periodic_inform_base_seconds: 3600,
  periodic_inform_spread_seconds: 900,
  optical_warn_rx_dbm: FIXTURE_OPTICAL_THRESHOLDS.warn,
  optical_critical_rx_dbm: FIXTURE_OPTICAL_THRESHOLDS.critical,
  cr_rate_limit_per_sec: 50,
  pon_capacity_onus_per_port: FIXTURE_PON_CAPACITY.default,
  // W8 (2026-11-04): the fleet watch's two inform rules — `stale_inform_hours` is the
  // «X ساعت Inform نداده» threshold the sync pass judges every device row against, and
  // `inform_flood_per_hour` is the «CPE پرحرف» rate that raises device.inform_flood.
  stale_inform_hours: 24,
  inform_flood_per_hour: 120,
};

/** `routers/system.py::OPS_SETTING_RANGES` — the inclusive per-key bounds the door validates against. */
export const FIXTURE_OPS_SETTING_RANGES: Readonly<Record<string, readonly [number, number]>> = {
  periodic_inform_base_seconds: [60, 86400],
  periodic_inform_spread_seconds: [0, 3600],
  optical_warn_rx_dbm: [-60, 0],
  optical_critical_rx_dbm: [-60, 0],
  cr_rate_limit_per_sec: [1, 1000],
  pon_capacity_onus_per_port: [FIXTURE_PON_CAPACITY.min, FIXTURE_PON_CAPACITY.max],
  stale_inform_hours: [2, 720],
  inform_flood_per_hour: [10, 10000],
};

/** `acs_service/ops_settings.py::SETTINGS_INTENT_NOTE`, verbatim — the honesty clause travels with the payload. */
export const FIXTURE_SETTINGS_INTENT_NOTE =
  'Operator intent: these values are recorded in acs_meta (ops_settings). ' +
  'Three are applied live: the two optical thresholds by the optical sweep ' +
  '(workers/optical_worker.py — it reads a sample per associated device and ' +
  'raises optical.degraded when RX crosses them), and ' +
  'pon_capacity_onus_per_port by the PON capacity door ' +
  '(GET /ops/v1/olts/{olt_id}/pon-capacity), which measures the occupancy the ' +
  'adapter read against this DECLARED capacity — it is not a device reading. ' +
  'The other two are intent only: pushing them into GenieACS presets is the ' +
  'C5 worker, which is not wired in this build. ' +
  'W8 adds two keys the fleet watch reads live: stale_inform_hours is the ' +
  '«X ساعت Inform نداده» threshold the sync pass judges every device row ' +
  'against (workers/sync_worker.watch_once), and inform_flood_per_hour is ' +
  'the «CPE پرحرف» rate that raises device.inform_flood from the recorded ' +
  'informs.';

/** `routers/system.py::SETTINGS_AUDIT_KIND` — the kind one accepted PUT writes into the ledger. */
export const FIXTURE_SETTINGS_AUDIT_KIND = 'ops.settings.update';

/**
 * The seeded user table.
 *
 * `owner-demo`/`admin-demo`/`operator-demo`/`user-demo` are the four demo accounts of
 * `fixtures/session.ts` seen as rows (same usernames, same roles), and `owner-alt-demo` is the second
 * active owner described in the header. The ids are stable so a test can address a row without reading
 * a generated one, and every timestamp is a constant: a fixture that called `new Date()` here would
 * make the table's own ordering vary between runs.
 */
export const FIXTURE_CONSOLE_USER_ROWS: readonly ConsoleUser[] = [
  {
    user_id: 'user-owner-demo',
    username: 'owner-demo',
    role: 'owner',
    status: 'active',
    created_at: '2026-10-01T00:00:00+00:00',
    updated_at: '2026-10-12T09:30:00+00:00',
  },
  {
    // The second active owner: what makes the last-owner guard demonstrable in both directions.
    user_id: 'user-owner-alt-demo',
    username: 'owner-alt-demo',
    role: 'owner',
    status: 'active',
    created_at: '2026-10-04T00:00:00+00:00',
    updated_at: '2026-10-14T18:05:00+00:00',
  },
  {
    user_id: 'user-admin-demo',
    username: 'admin-demo',
    role: 'admin',
    status: 'active',
    created_at: '2026-10-05T00:00:00+00:00',
    updated_at: '2026-10-05T00:00:00+00:00',
  },
  {
    user_id: 'user-operator-demo',
    username: 'operator-demo',
    role: 'operator',
    status: 'active',
    created_at: '2026-10-06T00:00:00+00:00',
    updated_at: '2026-10-11T07:15:00+00:00',
  },
  {
    user_id: 'user-user-demo',
    username: 'user-demo',
    role: 'user',
    status: 'active',
    created_at: '2026-10-07T00:00:00+00:00',
    updated_at: '2026-10-07T00:00:00+00:00',
  },
  {
    // A disabled row is part of the demo: §۳-۲ does not protect it (it is not a way in), and the
    // table an operator reads must show both statuses rather than only the healthy one.
    user_id: 'user-former-demo',
    username: 'former-operator-demo',
    role: 'operator',
    status: 'disabled',
    created_at: '2026-09-20T00:00:00+00:00',
    updated_at: '2026-10-09T12:00:00+00:00',
  },
];

/**
 * `identity.py::last_owner_allows`, ported.
 *
 * `resulting` is the row as it would stand after the write; a removal passes an object carrying only
 * `user_id` (that is what `delete_user_guarded` hands the guard). The three clauses of the server's
 * sentence — another active owner exists, the write leaves an active owner, the row was never an active
 * owner — are the whole rule, and nothing is added here: a fixture that also refused a password
 * rotation would teach the page a rule the door does not have.
 */
export function lastOwnerAllows(
  users: readonly ConsoleUser[],
  resulting: Partial<ConsoleUser>,
): boolean {
  const userId = String(resulting.user_id ?? '');
  const wasActiveOwner = users.some(
    (user) => String(user.user_id) === userId && user.role === 'owner' && user.status === 'active',
  );
  const anotherActiveOwner = users.some(
    (user) => String(user.user_id) !== userId && user.role === 'owner' && user.status === 'active',
  );
  const stillActiveOwner = resulting.role === 'owner' && resulting.status === 'active';
  return anotherActiveOwner || stillActiveOwner || !wasActiveOwner;
}

/**
 * The door's refusal sentence, as `routers/identity.py:457-459` and `:512-515` write it.
 *
 * `!r` in Python is a single-quoted repr, so the username is quoted the way the server quotes it — the
 * page renders this string and nothing else, which is the point of the last-owner test.
 */
export function lastOwnerRefusal(username: string): string {
  return (
    `'${username}' is the only active owner on this deployment; promote another owner before ` +
    'demoting, disabling or deleting this one'
  );
}

/** The door's 409 for a taken username (`routers/identity.py:397-398`). */
export function userExistsRefusal(username: string): string {
  return `a console user named '${username}' already exists`;
}

/** `routers/identity.py::_validated_role`'s detail, with the vocabulary this deployment publishes. */
export function roleInvalidDetail(role: string): string {
  return `role must be one of ${FIXTURE_ROLES.join(', ')}, got '${role}'`;
}

/** `routers/identity.py::_validated_status`'s detail. */
export function statusInvalidDetail(status: string): string {
  return `status must be one of ${USER_STATUSES.join(', ')}, got '${status}'`;
}

/**
 * The mutable settings document the fixture's two settings doors share — the mirror of the store's one
 * `ops_settings` meta document (`Store.get_settings_kv` / `put_settings_kv`).
 *
 * Only keys an operator has actually written are present, because that is what makes a `GET` answer
 * `updated_at: null` before the first PUT: the read merges defaults over the stored rows
 * (`system.py::_settings_read`), it does not seed them.
 */
export interface FixtureSettingsState {
  stored: Record<string, number>;
  updated_at: string | null;
}

export function fixtureSettingsState(
  overrides?: Partial<FixtureSettingsState>,
): FixtureSettingsState {
  return { stored: { ...(overrides?.stored ?? {}) }, updated_at: overrides?.updated_at ?? null };
}

/** A fresh session copy of the user table — provider-local writes must not move the module fixture. */
export function cloneUserRows(
  users: readonly ConsoleUser[] = FIXTURE_CONSOLE_USER_ROWS,
): ConsoleUser[] {
  return users.map((user) => ({ ...user }));
}

/** The default-only settings read, i.e. a deployment where nobody has written intent yet. */
export function settingsReadFor(state: FixtureSettingsState): {
  values: Record<string, number>;
  defaults: Record<string, number>;
  updated_at: string | null;
  note: string;
} {
  const values: Record<string, number> = {};
  for (const [key, fallback] of Object.entries(FIXTURE_OPS_SETTING_DEFAULTS)) {
    values[key] = Number.isFinite(state.stored[key]) ? state.stored[key]! : fallback;
  }
  return {
    values,
    defaults: { ...FIXTURE_OPS_SETTING_DEFAULTS },
    updated_at: state.updated_at,
    note: FIXTURE_SETTINGS_INTENT_NOTE,
  };
}

/** Membership against `FIXTURE_ROLES` — the copy of `identity.ROLES` this fixture carries. */
export function isKnownRole(role: string): boolean {
  return (FIXTURE_ROLES as readonly string[]).includes(role);
}

/** Membership against `USER_STATUSES` — the copy of `routers/identity.py::USER_STATUSES`. */
export function isKnownStatus(status: string): boolean {
  return (USER_STATUSES as readonly string[]).includes(status);
}

/**
 * `system.py::_validate_settings`, ported: whitelist first, then integer, then the inclusive bounds.
 *
 * Returns the refusal's detail sentence, or `null` when the pair is admissible. The ORDER is the door's
 * — an unknown key names itself before anything else is judged, because a typo must not look like a
 * successful write of something else — and the bounds are the server's envelope, not a UI preference
 * (`test/settingsFixture.test.ts` compares these two tables against `routers/system.py` itself).
 */
export function settingsRejection(key: string, value: unknown): string | null {
  if (!(key in FIXTURE_OPS_SETTING_DEFAULTS)) {
    return (
      `unknown setting(s): ${key} — the whitelist is ` +
      `${Object.keys(FIXTURE_OPS_SETTING_DEFAULTS).sort().join(', ')}`
    );
  }
  if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value)) {
    return `${key} must be an integer`;
  }
  const bounds = FIXTURE_OPS_SETTING_RANGES[key];
  if (!bounds) return null;
  const [low, high] = bounds;
  return value < low || value > high ? `${key} must be within ${low}..${high}` : null;
}

/** Every unknown key of a body, in the door's own sorted sentence. */
export function unknownSettingsDetail(payload: Record<string, unknown>): string | null {
  const unknown = Object.keys(payload)
    .filter((key) => !(key in FIXTURE_OPS_SETTING_DEFAULTS))
    .sort();
  if (unknown.length === 0) return null;
  return (
    `unknown setting(s): ${unknown.join(', ')} — the whitelist is ` +
    `${Object.keys(FIXTURE_OPS_SETTING_DEFAULTS).sort().join(', ')}`
  );
}
